/**
 * PedidoList - Lista de pedidos de compra da aba "Pedidos" (extraído de Compras.jsx)
 * Componente de apresentação: recebe dados e handlers via props.
 */
import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Trash2, Send, CheckCircle, Eye, ShoppingCart,
  Package, Calendar, Download, CreditCard, RefreshCw,
} from 'lucide-react';
import { PagamentoBadge } from './pagamentoStatus';
import { formatCurrency } from './utils';
import { StatusBadge, StatusLegend } from './utils';
import { getCurrencyMeta } from '../../lib/currencies';

const PedidoList = ({
  pedidos,
  isAdmin,
  onViewPedido,
  onAprovarPedido,
  onEnviarPedido,
  onReceberPedido,
  onOpenParcelas,
  onOpenConferencia,
  onPreviewPdf,
  onDownloadPdf,
  onDeletePedido,
}) => {
  return (
    <>
      {/* Info sobre código */}
      <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
        <strong>Pedidos de Compra (PC):</strong> São gerados automaticamente quando você aprova um fornecedor vencedor em uma cotação. O código PC-ANO-XXXX é o número único do pedido.
      </div>

      {/* Legenda de status (recolhível) */}
      <StatusLegend grupo="pedido" />

      {pedidos.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">Nenhum pedido encontrado</h3>
            <p className="text-gray-500">Os pedidos serão criados a partir das cotações aprovadas</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {pedidos.map(pedido => (
            <Card key={pedido.id} className={`hover:shadow-md transition-shadow ${pedido.urgente ? 'border-red-500 border-2 bg-red-50 shadow-red-100 shadow-md ring-2 ring-red-200' : ''}`} data-testid={`pedido-card-${pedido.numero}`}>
              <CardContent className="py-4">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`font-mono text-sm font-medium ${pedido.urgente ? 'text-red-600' : 'text-purple-600'}`} title="Código do Pedido de Compra">{pedido.numero}</span>
                      <StatusBadge status={pedido.status} />
                      {pedido.urgente && (
                        <Badge className="bg-red-600 text-white hover:bg-red-600 animate-pulse font-bold px-3">
                          🚨 URGENTE
                        </Badge>
                      )}
                      {pedido.origem === 'ficha_tecnica' && (
                        <Badge variant="outline" className="text-purple-600 border-purple-300">
                          Ficha Técnica
                        </Badge>
                      )}
                      {pedido.projeto_nome && (
                        <Badge variant="outline" className="text-xs">
                          {pedido.projeto_nome}
                        </Badge>
                      )}
                      {(pedido.moeda && pedido.moeda !== 'BRL') && (
                        <Badge variant="outline" className="text-xs border-indigo-300 text-indigo-700" data-testid={`pedido-moeda-${pedido.numero}`}>
                          {getCurrencyMeta(pedido.moeda).symbol} {pedido.moeda}
                        </Badge>
                      )}
                      {/* Status Financeiro (baixa vinda do Conta Azul) */}
                      <PagamentoBadge pedido={pedido} />
                    </div>
                    <h3 className="font-medium text-gray-900">{pedido.fornecedor_nome}</h3>
                    <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Package className="w-4 h-4" />
                        {pedido.itens?.length || 0} itens
                      </span>
                      <span className="font-medium text-green-600">
                        {formatCurrency(pedido.total || 0, pedido.moeda || 'BRL')}
                      </span>
                      {(pedido.moeda && pedido.moeda !== 'BRL' && pedido.taxa_cambio > 0) && (
                        <span className="text-xs text-slate-400" data-testid={`pedido-equivalente-${pedido.numero}`}>
                          ≈ {formatCurrency((pedido.total || 0) * pedido.taxa_cambio, 'BRL')}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(pedido.created_at).toLocaleDateString('pt-BR')}
                      </span>
                      {/* Micro-resumo financeiro */}
                      {pedido.parcelas?.length > 0 && (
                        <span className="flex items-center gap-1 text-gray-400" data-testid={`pagamento-resumo-${pedido.numero}`}>
                          <CreditCard className="w-4 h-4" />
                          {(() => {
                            const pagas = (pedido.parcelas || []).filter(p => ['pago', 'quitado', 'recebido', 'liquidado'].includes((p.status || '').toLowerCase())).length;
                            return `${pagas}/${pedido.parcelas.length} pagas`;
                          })()}
                        </span>
                      )}
                      {pedido.recado_financeiro && (
                        <span className="flex items-center gap-1 text-amber-600" title={`Financeiro: ${pedido.recado_financeiro}`} data-testid={`recado-fin-tag-${pedido.numero}`}>
                          💬 {pedido.recado_financeiro.length > 28 ? pedido.recado_financeiro.slice(0, 28) + '…' : pedido.recado_financeiro}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Botão Ver Detalhes */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onViewPedido(pedido)}
                      data-testid={`btn-view-pedido-${pedido.id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Detalhes
                    </Button>

                    {/* Ações por Status */}
                    {pedido.status === 'pendente' && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => onAprovarPedido(pedido)}
                          className="bg-green-600 hover:bg-green-700"
                          data-testid={`btn-aprovar-pedido-${pedido.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Aprovar
                        </Button>
                      </>
                    )}

                    {pedido.status === 'aprovado' && (
                      <Button
                        size="sm"
                        onClick={() => onEnviarPedido(pedido)}
                        data-testid={`btn-enviar-pedido-${pedido.id}`}
                      >
                        <Send className="w-4 h-4 mr-1" />
                        Enviar ao Fornecedor
                      </Button>
                    )}

                    {(pedido.status === 'enviado' || pedido.status === 'recebido_parcial') && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onReceberPedido(pedido)}
                        data-testid={`btn-receber-pedido-${pedido.id}`}
                      >
                        <Package className="w-4 h-4 mr-1" />
                        Registrar Recebimento
                      </Button>
                    )}

                    {/* Botão Conferir Custos do Projeto - vinculado a projeto e já aprovado */}
                    {pedido.projeto_id && ['aprovado', 'enviado', 'recebido', 'recebido_parcial'].includes(pedido.status) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onOpenConferencia(pedido)}
                        title={pedido.custos_conferidos ? 'Custos já conferidos — clique para conferir novamente' : 'Conferir e distribuir custos na Ficha Técnica do projeto'}
                        data-testid={`btn-conferir-custos-${pedido.id}`}
                        className={pedido.custos_conferidos
                          ? 'text-green-700 border-green-300 hover:bg-green-50'
                          : 'text-amber-700 border-amber-400 bg-amber-50 hover:bg-amber-100'}
                      >
                        <RefreshCw className="w-4 h-4 mr-1" />
                        {pedido.custos_conferidos ? 'Custos ✓' : 'Conferir Custos'}
                      </Button>
                    )}

                    {/* PDF sempre disponível */}
                    <div className="flex gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onPreviewPdf('pedido', pedido.id, pedido.numero)}
                        title="Visualizar PDF"
                        data-testid={`btn-preview-pedido-${pedido.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        PDF
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDownloadPdf(pedido.id, pedido.numero)}
                        title="Baixar PDF"
                        data-testid={`btn-pdf-pedido-${pedido.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Botão Excluir - usuários de compras (backend valida permissão) */}
                    {pedido.status !== 'excluido' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDeletePedido(pedido.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`btn-delete-pedido-${pedido.id}`}
                        title="Excluir pedido"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </>
  );
};

export default PedidoList;
